/* Salt XO — Subscribe & Save toggle inside the Zipify OCU cart drawer
 * ---------------------------------------------------------------------------
 * Loaded (deferred) from snippets/oneclickupsellapp-extend.liquid, so it only
 * runs where OCU itself runs: published live theme, OCU app installed
 * (theme.liquid gates that snippet off previews / non-main themes).
 *
 * The OCU drawer is a Vue app rendered from Zipify's CDN bundle — Liquid
 * can't reach inside it. This script fetches ready-rendered widget markup
 * from our Section Rendering API endpoint
 * (sections/ocu-cart-subscription-data.liquid, which re-renders
 * snippets/cart-subscription-toggle.liquid per cart line) and clones it into
 * each drawer line item. Styling comes from the `.salt-sub-toggle*` block in
 * assets/custom.css — same classes as the native drawer widget.
 *
 * Contracts with Zipify's code (verified against their shipped bundles,
 * zipify-cart-drawer.js integration layer + zipify-cart-drawer-app.*.js Vue
 * app, 2025/11/11 cart version — re-verify these if the widget ever stops
 * appearing after a Zipify update):
 *
 *   1. Each drawer line renders as
 *      `.ocu-cart-line-item-wrapper[data-key="<cart line key>"]` — a
 *      one-column CSS grid (10px row-gap). Appending our widget as its last
 *      child yields a full-width row under the line info. `data-key` gives
 *      exact DOM↔cart-line matching; no index heuristics.
 *   2. A line mid-add renders `.ocu-ghost-product` inside the wrapper and
 *      its key isn't a real cart key yet — skipped naturally + explicitly.
 *   3. Zipify patches window.fetch (interceptCartMutations): any fetch whose
 *      response URL contains /cart/change, /cart/update or /cart/clear —
 *      and does NOT contain "?ocu" — makes THEM parse the response and
 *      dispatch `ocu:cart:update` with the fresh cart, re-rendering the
 *      drawer. So our plain POST to /cart/change.js is also the drawer
 *      refresh: no private API calls needed.
 *   4. Drawer lifecycle events (their public names): ocu:cart:loaded,
 *      ocu:cart:opened, ocu:cart:update (inbound re-sync), ocu:cart:updated.
 *      We refetch templates on the update events because a cart change makes
 *      the rendered checked/selected state stale.
 *   5. `Zipify.Cart.cart` mirrors the Shopify cart (kept fresh by their
 *      interceptor) — used to read a line's quantity without an extra
 *      /cart.js round-trip, with /cart.js as fallback.
 *
 * Vue re-renders may drop or keep our injected node depending on patch path.
 * Either is fine: a MutationObserver re-runs sync(), which re-injects when
 * the widget is missing and replaces it when stale (revision marker). sync()
 * is idempotent, so observer loops settle immediately.
 *
 * The change listener only claims widgets INSIDE `.ocu-cart-line-item-wrapper`
 * — the native drawer's copy of this widget is handled by
 * snippets/cart-subscription-script.liquid, which owns everything outside the
 * OCU drawer. Keep the two scopes mutually exclusive or a single change
 * would POST twice.
 *
 * Resilience layer — added after OCU's partial fix. Zipify's update cascade
 * can throw mid-chain (their acknowledged first-add bug; their devs applied
 * blind hardening but could not reproduce all errors). A crash there can
 * suppress the very ocu:* events we re-sync on, so events alone are not a
 * reliable trigger. Three time-based nets close that:
 *   - settle passes (600ms / 2000ms) after any cart activity re-check the
 *     drawer and repair missing/stale widgets;
 *   - a 3s watchdog runs the same check while a drawer exists, healing
 *     anything that settled without firing an event;
 *   - a window error listener counts crashes originating in Zipify's
 *     drawer bundles (window.SaltOcuSubToggle.stats) and triggers an early
 *     settle — their crash becomes our repair signal.
 * All passes are idempotent no-ops when the drawer is healthy.
 *
 * v1.2 — optimistic UI. State changes paint instantly (checkbox, active
 * class, select visibility via CSS) while the POST runs in the background;
 * reconciliation swaps in server-truth markup afterwards, which is a visual
 * no-op on success and an automatic revert on failure. The widget is never
 * disabled or dimmed; a per-line last-write-wins queue absorbs rapid
 * toggling, re-locating the line after each response because selling-plan
 * changes rotate the cart line key. Requires the snippet to always render
 * the <select> (hidden on one-time lines by custom.css).
 *
 * v1.3 — no vanish on line re-mounts. sync() no longer awaits the section
 * refetch (it paints from the current cache; fetch completion re-syncs),
 * and after every /cart/change the old template is re-keyed client-side to
 * the rotated line key with the new state applied (retargetTemplate), so
 * the widget re-appears within a frame of Zipify re-mounting the line
 * instead of after a full section round-trip. What remains visible is
 * Zipify re-rendering the line itself (image/price refresh) — platform
 * behavior for a plan change, identical to their own stepper's refresh.
 *
 * v1.4 — dynamic labels + slashed prices (client feedback 2026-08-06).
 * The widget's two label states are server-rendered and CSS-switched via
 * --active; only the percentage number is JS-updated from the selected
 * option's data-discount-pct. decoratePrices() (called from sync + settle)
 * prepends a slashed one-time price inside Zipify's line price container
 * for subscribed lines and a slashed original grand total next to the
 * footer total, both computed from selling_plan_allocation.compare_at_price
 * in Zipify's live cart mirror. The footer total has no dedicated class in
 * their DOM — anchored on the last leaf element rendering the formatted
 * total; if their markup changes, the decoration silently skips.
 *
 * Debug: append ?salt_sub_debug to the URL (or set window.__saltOcuSubDebug)
 * for console logging. window.SaltOcuSubToggle.{sync,invalidate,disable}()
 * and .stats are exposed for live-session debugging.
 */
(() => {
  'use strict';
  if (window.__saltOcuSubInit) return;
  window.__saltOcuSubInit = true;

  const SECTION_ID = 'ocu-cart-subscription-data';
  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const WRAPPER_SEL = '.ocu-cart-line-item-wrapper[data-key]';
  const WIDGET_SEL = '[data-salt-sub-toggle]';
  const DEBUG = /[?&]salt_sub_debug/.test(location.search) || Boolean(window.__saltOcuSubDebug);

  const log = (...args) => {
    if (DEBUG) console.info('[salt-ocu-sub]', ...args);
  };

  let templates = new Map(); // line key -> <template> from the section endpoint
  let knownKeys = new Set(); // every line key the section reported, widget or not
  let revision = 0;          // bumped whenever the cart may have changed
  let fetchedRevision = -1;  // revision the in-flight/last fetch was REQUESTED at
  let appliedRevision = -1;  // revision the current `templates` map actually holds
  let fetchPromise = null;
  let syncScheduled = false;
  let settleTimers = [];
  let lastUnknownRev = -1;   // one auto-invalidate per revision for unknown keys

  const stats = { syncs: 0, injections: 0, decorations: 0, zipifyErrors: 0, lastZipifyError: null };

  function invalidate() {
    revision += 1;
    scheduleSync();
  }

  function ensureTemplates() {
    if (fetchedRevision === revision && fetchPromise) return fetchPromise;
    const target = revision;
    fetchedRevision = target;
    fetchPromise = fetch(`${ROOT}?sections=${SECTION_ID}`, { cache: 'no-store' })
      .then((res) => {
        if (!res.ok) throw new Error(`section fetch ${res.status}`);
        return res.json();
      })
      .then((json) => {
        const html = json[SECTION_ID];
        if (typeof html !== 'string') throw new Error('section payload missing');
        // A newer fetch superseded this one while it was in flight — its
        // result would clobber fresher markup, so drop it.
        if (fetchedRevision !== target) return templates;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const map = new Map();
        const keys = new Set();
        doc.querySelectorAll('template[data-line-key]').forEach((tpl) => {
          keys.add(tpl.dataset.lineKey);
          // Empty template = product has no Skio plan group = no widget.
          if (tpl.content.querySelector(WIDGET_SEL)) map.set(tpl.dataset.lineKey, tpl);
        });
        templates = map;
        knownKeys = keys;
        appliedRevision = target;
        log('templates fetched', map.size, 'of', keys.size, 'lines, rev', target);
        // sync() is non-blocking and may have painted from the previous map
        // (or nothing) — re-run it now that fresh markup is in.
        scheduleSync();
        return map;
      })
      .catch((err) => {
        // Reset so the next sync retries instead of trusting a failed fetch.
        if (fetchedRevision === target) fetchedRevision = -1;
        log('template fetch failed', err);
        return templates;
      });
    return fetchPromise;
  }

  function sync() {
    const wrappers = document.querySelectorAll(WRAPPER_SEL);
    if (!wrappers.length) return;
    // Kick a refetch when stale, but NEVER wait for it — paint from the
    // current map immediately (a plan change re-mounts the whole line in
    // Zipify's Vue tree, and blocking here is what made the widget vanish
    // until the section round-trip returned). The fetch's completion
    // re-runs sync() and swaps in server truth.
    ensureTemplates();
    if (appliedRevision < 0) return; // nothing fetched yet — completion re-syncs
    stats.syncs += 1;
    const rev = String(appliedRevision);
    wrappers.forEach((wrapper) => {
      // One bad wrapper must never block the rest of the drawer.
      try {
        if (wrapper.querySelector('.ocu-ghost-product')) return; // mid-add placeholder
        const tpl = templates.get(wrapper.dataset.key);
        const existing = wrapper.querySelector(`:scope > ${WIDGET_SEL}`);
        if (!tpl) {
          if (existing) existing.remove();
          return;
        }
        if (existing && existing.dataset.saltRev === rev) return; // current — leave alone
        if (existing) existing.remove();
        const fragment = tpl.content.cloneNode(true);
        const widget = fragment.querySelector(WIDGET_SEL);
        widget.dataset.saltRev = rev;
        wrapper.appendChild(fragment);
        stats.injections += 1;
        log('injected', wrapper.dataset.key);
      } catch (err) {
        log('wrapper sync failed', err);
      }
    });
    decoratePrices();
  }

  // Anomaly check used by the settle passes and the watchdog. Detects two
  // states an event-driven sync can miss when Zipify's cascade dies mid-way:
  //   - a wrapper whose key our section snapshot has never seen (a line was
  //     added but no ocu:* event reached us) -> refetch templates;
  //   - a known widget-bearing key with no widget in the DOM (their re-render
  //     dropped our node and the observer pass lost the race) -> re-sync.
  // Guarded to one auto-refetch per revision so a healthy-but-unusual cart
  // (e.g. all gift lines) cannot loop fetches.
  function settlePass() {
    const wrappers = document.querySelectorAll(WRAPPER_SEL);
    if (!wrappers.length) return;
    let missingWidget = false;
    let unknownKey = false;
    wrappers.forEach((wrapper) => {
      if (wrapper.querySelector('.ocu-ghost-product')) return;
      const props = wrapper.getAttribute('data-properties') || '';
      if (props.indexOf('_ocu_free_product_tier_id') !== -1) return; // gift line — never has a widget
      const key = wrapper.dataset.key;
      if (!knownKeys.has(key)) {
        unknownKey = true;
        return;
      }
      if (templates.has(key) && !wrapper.querySelector(`:scope > ${WIDGET_SEL}`)) missingWidget = true;
    });
    if (unknownKey && lastUnknownRev !== revision) {
      lastUnknownRev = revision;
      log('settle: unknown line key — refetching');
      invalidate();
      return;
    }
    if (missingWidget) {
      log('settle: widget missing — re-syncing');
      scheduleSync();
    }
    decoratePrices();
  }

  function scheduleSettle() {
    settleTimers.forEach(clearTimeout);
    settleTimers = [600, 2000].map((ms) => setTimeout(settlePass, ms));
  }

  function scheduleSync() {
    if (syncScheduled) return;
    syncScheduled = true;
    requestAnimationFrame(() => {
      syncScheduled = false;
      try {
        sync();
      } catch (err) {
        log('sync failed', err);
      }
    });
  }

  async function lineInfo(lineKey) {
    const zipItems =
      window.Zipify && window.Zipify.Cart && window.Zipify.Cart.cart && window.Zipify.Cart.cart.items;
    if (Array.isArray(zipItems)) {
      const line = zipItems.find((item) => item.key === lineKey);
      if (line) return { quantity: line.quantity, variantId: line.variant_id };
    }
    const cart = await fetch(`${ROOT}cart.js`, { cache: 'no-store' }).then((res) => res.json());
    const line = cart.items.find((item) => item.key === lineKey);
    if (!line) throw new Error('line not in cart');
    return { quantity: line.quantity, variantId: line.variant_id };
  }

  function planIdOf(item) {
    return String(
      (item.selling_plan_allocation &&
        item.selling_plan_allocation.selling_plan &&
        item.selling_plan_allocation.selling_plan.id) ||
        ''
    );
  }

  // v1.2 optimistic UI. The widget reflects the shopper's choice instantly;
  // the POST runs in the background and Zipify's re-render + our template
  // refetch reconcile to server truth — visually a no-op on success, an
  // automatic revert on failure. In-flight bookkeeping lives OUTSIDE the
  // DOM because a sync can replace the widget node mid-request. One runner
  // per line; `desired` is the newest requested plan (last write wins), so
  // rapid toggling settles on the final choice without lost or duplicate
  // POSTs.
  const pending = new Map(); // lineKey -> { inFlight, desired }

  function applyOptimistic(widget, planId) {
    widget.classList.toggle('salt-sub-toggle--active', Boolean(planId));
    widget.classList.add('salt-sub-toggle--busy');
    widget.setAttribute('aria-busy', 'true');
    const check = widget.querySelector('.salt-sub-toggle__check');
    if (check) check.checked = Boolean(planId);
    const select = widget.querySelector('.salt-sub-toggle__plan');
    if (select && planId) select.value = String(planId);
    // Which label shows ("Up To max%" vs "You're Saving pct%") is pure CSS
    // via --active; only the pct number needs updating to the selected plan.
    const pctNode = widget.querySelector('[data-salt-pct]');
    if (pctNode && select) {
      const opt = select.selectedOptions && select.selectedOptions[0];
      const pct = opt && opt.getAttribute('data-discount-pct');
      if (pct && pctNode.textContent !== pct) pctNode.textContent = pct;
    }
  }

  // ---- Slashed original prices (subscribed lines + grand total) -----------
  // "Was" data source is the selling plan allocation's compare_at_price
  // (the one-time price, in cents) from Zipify's live cart mirror — their
  // interceptor keeps it fresh, and every path that changes the cart already
  // funnels through sync()/settlePass(), so decorations ride the existing
  // machinery with no extra observers. All operations are idempotent.
  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  function cartSnapshot() {
    const cart = window.Zipify && window.Zipify.Cart && window.Zipify.Cart.cart;
    return cart && Array.isArray(cart.items) ? cart : null;
  }

  function decoratePrices() {
    const cart = cartSnapshot();
    if (!cart) return;
    document.querySelectorAll(WRAPPER_SEL).forEach((wrapper) => {
      const container = wrapper.querySelector('.ocu-cart-line-price-container');
      if (!container) return;
      const item = cart.items.find((it) => it.key === wrapper.dataset.key);
      const alloc = item && item.selling_plan_allocation;
      const wasCents = alloc && alloc.compare_at_price ? alloc.compare_at_price * item.quantity : 0;
      const existing = container.querySelector('.salt-ocu-was-price');
      // Stay out of the way when Zipify renders its own was-price (their
      // discount / compare-at feature) — never show two slashes.
      const theirs = container.querySelector('.ocu-cart-line-price-was');
      if (!item || !wasCents || theirs || wasCents <= item.final_line_price) {
        if (existing) existing.remove();
        return;
      }
      const text = formatMoney(wasCents);
      if (existing) {
        if (existing.textContent !== text) existing.textContent = text;
        return;
      }
      const span = document.createElement('span');
      span.className = 'salt-ocu-was-price';
      span.textContent = text;
      container.insertBefore(span, container.firstChild);
      stats.decorations += 1;
      log('line was-price decorated', wrapper.dataset.key, text);
    });
    decorateTotal(cart);
  }

  function decorateTotal(cart) {
    const drawer = document.querySelector('.ocu-cart-drawer');
    if (!drawer) return;
    let originalTotal = 0;
    cart.items.forEach((item) => {
      const alloc = item.selling_plan_allocation;
      originalTotal +=
        alloc && alloc.compare_at_price
          ? alloc.compare_at_price * item.quantity
          : item.final_line_price;
    });
    const existing = drawer.querySelector('.salt-ocu-was-total');
    if (originalTotal <= cart.total_price) {
      if (existing) existing.remove();
      return;
    }
    // Zipify's footer total row has no dedicated class — anchor on the LAST
    // leaf element showing the formatted grand total (line prices can match
    // the same string in a one-line cart, but the footer renders below the
    // lines, so last-in-document wins). Skip silently if nothing matches.
    const totalText = formatMoney(cart.total_price);
    const candidates = [...drawer.querySelectorAll('span, div, p, b, strong')].filter(
      (el) =>
        el.children.length === 0 &&
        !el.closest('.ocu-cart-line-price-container') &&
        !el.closest(WIDGET_SEL) &&
        !el.classList.contains('salt-ocu-was-total') &&
        el.textContent.indexOf(totalText) !== -1
    );
    const target = candidates[candidates.length - 1];
    if (!target) {
      if (existing) existing.remove();
      log('total anchor not found');
      return;
    }
    const wasText = formatMoney(originalTotal);
    if (existing) {
      if (existing.nextSibling === target && existing.textContent === wasText) return;
      existing.remove(); // re-render moved/changed things — re-place fresh
    }
    const span = document.createElement('span');
    span.className = 'salt-ocu-was-total';
    span.textContent = wasText;
    target.parentNode.insertBefore(span, target);
    stats.decorations += 1;
    log('total was-price decorated', wasText, 'next to', totalText);
  }

  // A selling-plan change ROTATES the cart line key, so Zipify's keyed Vue
  // list unmounts the old line and mounts a new one — taking our widget with
  // it. The /cart/change response already names the new key and state, and
  // the old key's template differs from the new state only in checked/
  // selected/active markers (same product, same plan options). So: clone the
  // old template, apply the new state, register it under the new key. The
  // very next sync paints the new wrapper within a frame — no waiting for
  // the section round-trip — and the real refetch swaps in server truth
  // invisibly (appliedRevision bump).
  function retargetTemplate(oldKey, newKey, planId) {
    const src = templates.get(oldKey);
    if (!src) return;
    const tpl = src.cloneNode(true);
    tpl.dataset.lineKey = newKey;
    const root = tpl.content.querySelector(WIDGET_SEL);
    if (!root) return;
    root.setAttribute('data-line-key', newKey);
    root.classList.remove('salt-sub-toggle--busy');
    if (planId) root.classList.add('salt-sub-toggle--active');
    else root.classList.remove('salt-sub-toggle--active');
    const check = tpl.content.querySelector('.salt-sub-toggle__check');
    if (check) {
      if (planId) check.setAttribute('checked', '');
      else check.removeAttribute('checked');
    }
    tpl.content.querySelectorAll('.salt-sub-toggle__plan option').forEach((opt) => {
      if (String(opt.getAttribute('value')) === String(planId || '')) opt.setAttribute('selected', '');
      else opt.removeAttribute('selected');
    });
    templates.set(newKey, tpl);
    knownKeys.add(newKey);
    log('retargeted template', oldKey, '->', newKey);
  }

  async function changeLine(lineKey, sellingPlanId) {
    const entry = pending.get(lineKey) || { inFlight: false, desired: null };
    entry.desired = sellingPlanId || null;
    pending.set(lineKey, entry);
    if (entry.inFlight) return; // the running loop below picks up `desired`
    entry.inFlight = true;
    try {
      let key = lineKey;
      for (;;) {
        const target = entry.desired;
        const info = await lineInfo(key);
        const res = await fetch(`${ROOT}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity: info.quantity, selling_plan: target }),
        });
        if (!res.ok) throw new Error(`cart/change ${res.status}`);
        const cartJson = await res.json();
        // Locate the line under its (possibly rotated) new key and register
        // an interim template for it so the re-mounted wrapper paints
        // immediately. Zipify's fetch interceptor has already seen this
        // response and is re-rendering the drawer (contract 3).
        const next = (cartJson.items || []).find(
          (item) => item.variant_id === info.variantId && planIdOf(item) === String(target || '')
        );
        if (next && next.key !== key) retargetTemplate(key, next.key, target);
        // Refetch replaces interim/optimistic markup with server truth;
        // settle passes cover the case where Zipify's re-render chain
        // throws part-way through.
        invalidate();
        scheduleSettle();
        if (entry.desired === target) break;
        if (!next) break; // line merged/removed — reconciliation shows truth
        key = next.key;
      }
    } catch (err) {
      log('change failed', err);
      // Refetch replaces the optimistic state with server truth (revert).
      invalidate();
      scheduleSettle();
    } finally {
      entry.inFlight = false;
      if (pending.get(lineKey) === entry) pending.delete(lineKey);
    }
  }

  document.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    const action = target.getAttribute('data-salt-sub-action');
    if (!action) return;
    const widget = target.closest(WIDGET_SEL);
    if (!widget) return;
    const wrapper = widget.closest('.ocu-cart-line-item-wrapper');
    if (!wrapper) return; // native drawer copy — cart-subscription-script.liquid owns it
    const lineKey = wrapper.dataset.key || widget.getAttribute('data-line-key');
    if (!lineKey) return;

    if (action === 'toggle') {
      const checked = target instanceof HTMLInputElement && target.checked;
      const planId = checked ? widget.getAttribute('data-default-plan-id') : null;
      applyOptimistic(widget, planId);
      changeLine(lineKey, planId);
    } else if (action === 'change-plan') {
      const planId = target instanceof HTMLSelectElement ? target.value : null;
      applyOptimistic(widget, planId);
      changeLine(lineKey, planId);
    }
  });

  // Cart-state events → templates are stale. ocu:product:added covers adds
  // (PDP and OCU's own in-drawer upsells), which never pass through their
  // /cart/change interceptor. Every cart-activity signal also arms the
  // settle passes, because the event that *should* follow may never fire if
  // Zipify's cascade crashes. Drawer-open → just make sure widgets exist.
  ['ocu:cart:update', 'ocu:cart:updated', 'ocu:cart:loaded', 'ocu:product:added'].forEach((name) =>
    addEventListener(name, () => {
      invalidate();
      scheduleSettle();
    })
  );
  addEventListener('ocu:cart:opened', scheduleSync);
  // bfcache restore can bring back a drawer whose cart is outdated.
  addEventListener('pageshow', (event) => {
    if (event.persisted) invalidate();
  });

  // Count crashes originating in Zipify's drawer bundles and use them as a
  // repair signal: if their pipeline just died, whatever it was mid-way
  // through updating may be stale — settle soon after.
  addEventListener('error', (event) => {
    if (typeof event.filename === 'string' && event.filename.indexOf('zipify-cart-drawer') !== -1) {
      stats.zipifyErrors += 1;
      stats.lastZipifyError = { message: event.message, at: new Date().toISOString() };
      log('zipify drawer error observed', event.message);
      scheduleSettle();
    }
  });

  // Last-resort self-heal: while a drawer exists, periodically run the same
  // anomaly check the settle passes use. No-ops entirely when healthy.
  setInterval(() => {
    if (document.hidden) return;
    if (!document.querySelector('.ocu-cart-drawer')) return;
    settlePass();
  }, 3000);

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      const target = mutation.target;
      // Our own injections mutate wrappers too; sync() is idempotent so the
      // extra pass no-ops. Skip only mutations fully inside a widget (e.g.
      // disabling controls) to avoid pointless passes.
      if (target instanceof Element && target.closest && target.closest(WIDGET_SEL)) continue;
      scheduleSync();
      return;
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });

  // Warm the template cache off the critical path so the first drawer open
  // paints the widget without waiting on the section round-trip.
  const warm = () => ensureTemplates();
  if ('requestIdleCallback' in window) requestIdleCallback(warm, { timeout: 4000 });
  else setTimeout(warm, 2500);

  window.SaltOcuSubToggle = {
    sync: scheduleSync,
    invalidate,
    settle: scheduleSettle,
    stats,
    disable() {
      observer.disconnect();
      document.querySelectorAll(`.ocu-cart-line-item-wrapper > ${WIDGET_SEL}`).forEach((el) => el.remove());
      document.querySelectorAll('.salt-ocu-was-price, .salt-ocu-was-total').forEach((el) => el.remove());
      log('disabled');
    },
  };

  scheduleSync();
})();
